import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Envelope, Phone, User, Clock, PaperPlaneTilt, PauseCircle, PlayCircle, CheckCircle, XCircle, GearSix } from "@phosphor-icons/react";

const STATUS_COLORS = {
  new:       "bg-sky-100 text-sky-700",
  contacted: "bg-violet-100 text-violet-700",
  responded: "bg-emerald-100 text-emerald-700",
  converted: "bg-emerald-500 text-white",
  dropped:   "bg-zinc-200 text-zinc-500",
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { hour12: false }) : "—";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // {leadId, templateId}
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (statusFilter) params.set("status", statusFilter);
      const [ls, ts] = await Promise.all([
        api.get(`/admin/leads?${params}`),
        api.get(`/admin/leads/templates`),
      ]);
      setLeads(ls.data.items || []);
      setTotal(ls.data.total || 0);
      setTemplates(ts.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const updateStatus = async (id, patch) => {
    try {
      await api.patch(`/admin/leads/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const sendNow = async (leadId, templateId) => {
    setSending({ leadId, templateId });
    try {
      const r = await api.post(`/admin/leads/${leadId}/send/${templateId}`);
      if (r.data?.entry?.error) throw new Error(r.data.entry.error);
      toast.success("Email sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Send failed");
    } finally { setSending(null); }
  };

  const tick = async () => {
    try {
      const r = await api.post(`/admin/leads/drip-tick`);
      toast.success(`Tick: picked ${r.data.picked}, sent ${r.data.sent}`);
      load();
    } catch (e) { toast.error("Tick failed"); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-leads-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]">Leads Inbox</h1>
          <span className="text-xs font-mono font-bold text-zinc-500">{total}</span>
          <div className="ml-auto flex gap-2">
            <Link to="/admin/leads/templates"
              className="inline-flex items-center gap-1 h-9 px-3 border-2 border-zinc-200 hover:border-[#6043EF] text-xs font-bold uppercase tracking-wider text-[#09090B] rounded-lg"
              data-testid="templates-link">
              <GearSix size={13} /> Templates
            </Link>
            <button onClick={tick} data-testid="tick-btn"
              className="inline-flex items-center gap-1 h-9 px-3 bg-[#09090B] hover:bg-[#6043EF] text-white text-xs font-bold uppercase tracking-wider rounded-lg">
              Trigger drip tick
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          {["", "new", "contacted", "responded", "converted", "dropped"].map((s) => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)} data-testid={`filter-${s || "all"}`}
              className={`h-7 px-3 text-[10px] font-bold uppercase tracking-wider rounded-full ${
                statusFilter === s ? "bg-[#6043EF] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}>
              {s || "All"}
            </button>
          ))}
        </div>

        {loading && <p className="text-sm text-zinc-500">Loading…</p>}

        <div className="border-2 border-zinc-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b-2 border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <tr><th className="px-3 py-2 text-left">Lead</th><th className="text-left">Rank</th><th className="text-left">College</th>
                <th className="text-left">Status</th><th className="text-left">Stage</th><th className="text-left">Next send</th><th className="text-left">Actions</th></tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <>
                <tr key={l._id} data-testid={`lead-row-${l._id}`} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => setExpanded(expanded === l._id ? null : l._id)}>
                  <td className="px-3 py-2">
                    <div className="font-bold text-[13px]">{l.name || "—"}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                      <Envelope size={10} /> {l.email || "—"} · <Phone size={10} /> {l.phone}
                    </div>
                  </td>
                  <td className="text-[12px] font-mono">{l.neet_rank || "—"}</td>
                  <td className="text-[12px]">{l.college_code || "—"}</td>
                  <td><span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${STATUS_COLORS[l.status] || "bg-zinc-100 text-zinc-500"}`}>{l.status || "new"}</span></td>
                  <td className="text-[11px] font-mono">{l.drip_stage ?? 0}</td>
                  <td className="text-[11px] font-mono text-zinc-500">{fmt(l.next_send_at)}</td>
                  <td>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {l.drip_paused ?
                        <button onClick={() => updateStatus(l._id, { drip_paused: false })} data-testid={`resume-${l._id}`} title="Resume drip" className="text-emerald-600 hover:bg-emerald-50 p-1 rounded"><PlayCircle size={16} weight="fill" /></button> :
                        <button onClick={() => updateStatus(l._id, { drip_paused: true })} data-testid={`pause-${l._id}`} title="Pause drip" className="text-amber-600 hover:bg-amber-50 p-1 rounded"><PauseCircle size={16} weight="fill" /></button>}
                      <button onClick={() => updateStatus(l._id, { status: "responded" })} data-testid={`mark-responded-${l._id}`} title="Mark responded" className="text-emerald-600 hover:bg-emerald-50 p-1 rounded"><CheckCircle size={16} weight="fill" /></button>
                      <button onClick={() => updateStatus(l._id, { status: "dropped" })} data-testid={`mark-dropped-${l._id}`} title="Drop lead" className="text-zinc-400 hover:bg-zinc-100 p-1 rounded"><XCircle size={16} weight="fill" /></button>
                    </div>
                  </td>
                </tr>
                {expanded === l._id && (
                  <tr className="bg-violet-50/40 border-t border-zinc-100">
                    <td colSpan={7} className="p-3">
                      <div className="grid grid-cols-2 gap-4 text-[12px]">
                        <div>
                          <div className="text-[10px] font-bold uppercase text-zinc-500 mb-1">Message</div>
                          <p className="text-zinc-700">{l.message || <i className="text-zinc-400">(none)</i>}</p>
                          <div className="mt-3 text-[10px] font-bold uppercase text-zinc-500 mb-1">Meta</div>
                          <div className="text-[11px] text-zinc-600 font-mono space-y-0.5">
                            <div>created: {fmt(l.created_at)}</div>
                            <div>source: {l.lead_source}</div>
                            {l.user_uid && <div>user_uid: {l.user_uid}</div>}
                            {l.user_email && <div>user_email: {l.user_email}</div>}
                            {l.responded_at && <div>responded: {fmt(l.responded_at)}</div>}
                            {l.response_signals && <div>signals: {Object.keys(l.response_signals).filter((k) => l.response_signals[k]).join(", ") || "—"}</div>}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase text-zinc-500 mb-1">Send Now</div>
                          <div className="space-y-1">
                            {templates.map((t) => (
                              <button key={t._id} onClick={() => sendNow(l._id, t._id)} disabled={sending?.leadId === l._id} data-testid={`send-${l._id}-${t._id}`}
                                className="w-full flex items-center gap-2 p-2 border border-zinc-200 hover:border-[#6043EF] rounded text-left text-[11px] disabled:opacity-50">
                                <PaperPlaneTilt size={12} weight="fill" className="text-[#6043EF] shrink-0" />
                                <span className="font-bold truncate">{t.name}</span>
                              </button>
                            ))}
                          </div>
                          <div className="mt-3 text-[10px] font-bold uppercase text-zinc-500 mb-1">Sent history</div>
                          <div className="space-y-1 text-[11px]">
                            {(l.sent_history || []).map((s, i) => (
                              <div key={i} className="flex items-center gap-2 p-1.5 bg-white border border-zinc-100 rounded">
                                <Clock size={10} className="text-zinc-400" />
                                <span className="font-bold flex-1 truncate">{s.template_name}</span>
                                <span className="text-zinc-400 font-mono text-[10px]">{fmt(s.sent_at)}</span>
                                {s.opened_at && <span title="opened" className="text-emerald-500">👁</span>}
                                {s.clicked_at && <span title="clicked" className="text-emerald-600">↗</span>}
                              </div>
                            ))}
                            {(!l.sent_history || l.sent_history.length === 0) && <p className="text-zinc-400 text-[10px]">(none yet)</p>}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </>
              ))}
              {leads.length === 0 && !loading && <tr><td colSpan={7} className="p-6 text-center text-zinc-400 text-sm">No leads yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
